import React, { useState } from 'react';
import { message } from 'antd';
import { PLATFORM_THEME, ALIGNMENT_STATUS } from '../../services/communications/communicationsTypes.js';
import {
  authorInitials,
  formatRelativeTime,
  highlightSearchTerms,
} from '../../services/communications/communicationsPresenters.js';

const PLANNER_STATUS = {
  not_started: { label: 'Not started', className: 'not-started' },
  in_progress: { label: 'In progress', className: 'in-progress' },
  complete: { label: 'Complete', className: 'complete' },
  '': { label: '', className: '' },
};

function openInApp(label) {
  message.info(`Microsoft integration coming soon — open in ${label}`);
}

function OutlookCard({ connection, items, searchQuery, activeThreadKey, onSelectItem }) {
  if (!connection?.connected) {
    return <EmptyAppState app="Outlook" />;
  }

  return (
    <section className="comm-app-card comm-app-card--outlook">
      <header className="comm-app-card__header">
        <div className="comm-app-card__brand">
          <span className="comm-app-card__app-icon" aria-hidden="true">✉</span>
          <div>
            <h2 className="comm-app-card__app-name">Outlook</h2>
            <p className="comm-app-card__context">{connection.name}</p>
          </div>
        </div>
        <div className="comm-app-card__header-actions">
          {connection.unreadCount != null ? (
            <span className="comm-app-card__pill">{connection.unreadCount} unread</span>
          ) : null}
          <button type="button" className="comm-app-card__link-btn" onClick={() => openInApp('Outlook')}>
            Open
          </button>
        </div>
      </header>

      <div className="comm-app-card__body comm-outlook-body">
        {items.length === 0 ? (
          <p className="comm-app-card__empty">No emails match your search.</p>
        ) : (
          <ul className="comm-outlook-list">
            {items.map((item) => (
              <li
                key={item.id}
                className={[
                  'comm-outlook-row',
                  activeThreadKey === item.threadKey ? 'thread-match' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => onSelectItem(item)}
              >
                <div className="comm-outlook-row__avatar">{authorInitials(item.author)}</div>
                <div className="comm-outlook-row__content">
                  <div className="comm-outlook-row__top">
                    <span className="comm-outlook-row__from">{item.author}</span>
                    <span className="comm-outlook-row__time">{formatRelativeTime(item.timestamp)}</span>
                  </div>
                  <div
                    className="comm-outlook-row__subject"
                    dangerouslySetInnerHTML={{ __html: highlightSearchTerms(item.subject, searchQuery) }}
                  />
                  <div
                    className="comm-outlook-row__preview"
                    dangerouslySetInnerHTML={{ __html: highlightSearchTerms(item.preview, searchQuery) }}
                  />
                  {item.decisionPosition ? (
                    <span className="comm-outlook-row__tag">{item.decisionPosition}</span>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function TeamsCard({ connection, items, searchQuery, activeThreadKey, onSelectItem }) {
  if (!connection?.connected) {
    return <EmptyAppState app="Teams" />;
  }

  return (
    <section className="comm-app-card comm-app-card--teams">
      <header className="comm-app-card__header">
        <div className="comm-app-card__brand">
          <span className="comm-app-card__app-icon" aria-hidden="true">💬</span>
          <div>
            <h2 className="comm-app-card__app-name">Microsoft Teams</h2>
            <p className="comm-app-card__context">{connection.name}</p>
          </div>
        </div>
        <div className="comm-app-card__header-actions">
          <button type="button" className="comm-app-card__link-btn" onClick={() => openInApp('Teams')}>
            Open
          </button>
        </div>
      </header>

      <div className="comm-teams-channels">
        <span className="comm-teams-channels__tab active">{connection.channel || '#general'}</span>
        <span className="comm-teams-channels__tab">#structural</span>
        <span className="comm-teams-channels__tab">#mep</span>
      </div>

      <div className="comm-app-card__body comm-teams-body">
        {items.length === 0 ? (
          <p className="comm-app-card__empty">No messages match your search.</p>
        ) : (
          <div className="comm-teams-feed">
            {items.map((item) => (
              <div
                key={item.id}
                className={[
                  'comm-teams-message',
                  activeThreadKey === item.threadKey ? 'thread-match' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => onSelectItem(item)}
              >
                <div className="comm-teams-message__avatar">{authorInitials(item.author)}</div>
                <div className="comm-teams-message__bubble">
                  <div className="comm-teams-message__meta">
                    <strong>{item.author}</strong>
                    <span>{formatRelativeTime(item.timestamp)}</span>
                  </div>
                  <p
                    className="comm-teams-message__text"
                    dangerouslySetInnerHTML={{ __html: highlightSearchTerms(item.preview || item.subject, searchQuery) }}
                  />
                  {item.decisionPosition ? (
                    <span className="comm-teams-message__tag">{item.decisionPosition}</span>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function PlannerCard({ connection, items, searchQuery, activeThreadKey, onSelectItem }) {
  if (!connection?.connected) {
    return <EmptyAppState app="Planner" />;
  }

  return (
    <section className="comm-app-card comm-app-card--planner">
      <header className="comm-app-card__header">
        <div className="comm-app-card__brand">
          <span className="comm-app-card__app-icon" aria-hidden="true">☑</span>
          <div>
            <h2 className="comm-app-card__app-name">Planner</h2>
            <p className="comm-app-card__context">{connection.name}</p>
          </div>
        </div>
        <div className="comm-app-card__header-actions">
          {connection.openTasks != null ? (
            <span className="comm-app-card__pill">{connection.openTasks} open</span>
          ) : null}
          <button type="button" className="comm-app-card__link-btn" onClick={() => openInApp('Planner')}>
            Open
          </button>
        </div>
      </header>

      <div className="comm-planner-buckets">
        <span className="comm-planner-buckets__label">Buckets</span>
        <span className="comm-planner-buckets__chip active">Decisions</span>
        <span className="comm-planner-buckets__chip">RFIs</span>
        <span className="comm-planner-buckets__chip">Deliverables</span>
      </div>

      <div className="comm-app-card__body comm-planner-body">
        {items.length === 0 ? (
          <p className="comm-app-card__empty">No tasks match your search.</p>
        ) : (
          <ul className="comm-planner-list">
            {items.map((item) => {
              const st = PLANNER_STATUS[item.status] || PLANNER_STATUS[''];
              return (
                <li
                  key={item.id}
                  className={[
                    'comm-planner-task',
                    activeThreadKey === item.threadKey ? 'thread-match' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => onSelectItem(item)}
                >
                  <span className={`comm-planner-task__check${item.status === 'complete' ? ' done' : ''}`} />
                  <div className="comm-planner-task__content">
                    <div
                      className="comm-planner-task__title"
                      dangerouslySetInnerHTML={{ __html: highlightSearchTerms(item.subject, searchQuery) }}
                    />
                    <p className="comm-planner-task__preview">{item.preview}</p>
                    <div className="comm-planner-task__footer">
                      {item.decisionPosition ? (
                        <span className="comm-planner-task__tag">{item.decisionPosition}</span>
                      ) : null}
                      {st.label ? (
                        <span className={`comm-planner-task__status comm-planner-task__status--${st.className}`}>
                          {st.label}
                        </span>
                      ) : null}
                      <span className="comm-planner-task__assignee">{item.author}</span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

function EmptyAppState({ app }) {
  return (
    <section className={`comm-app-card comm-app-card--${app.toLowerCase()} comm-app-card--empty`}>
      <p>Configure communications to connect {app}.</p>
    </section>
  );
}

export default function CommunicationsAppStack({
  connections,
  itemsBySource,
  visibleSources,
  searchQuery,
  activeThreadKey,
  onSelectItem,
}) {
  const showOutlook = visibleSources.includes('outlook');
  const showTeams = visibleSources.includes('teams');
  const showPlanner = visibleSources.includes('planner');

  return (
    <div className="comm-app-stack">
      {showOutlook ? (
        <OutlookCard
          connection={connections?.outlook}
          items={itemsBySource.outlook}
          searchQuery={searchQuery}
          activeThreadKey={activeThreadKey}
          onSelectItem={onSelectItem}
        />
      ) : null}
      {showTeams ? (
        <TeamsCard
          connection={connections?.teams}
          items={itemsBySource.teams}
          searchQuery={searchQuery}
          activeThreadKey={activeThreadKey}
          onSelectItem={onSelectItem}
        />
      ) : null}
      {showPlanner ? (
        <PlannerCard
          connection={connections?.planner}
          items={itemsBySource.planner}
          searchQuery={searchQuery}
          activeThreadKey={activeThreadKey}
          onSelectItem={onSelectItem}
        />
      ) : null}
    </div>
  );
}
